import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Home from "@/pages/Home";
import LanguagePairSelection from "@/pages/LanguagePairSelection";
import Dashboard from "@/pages/Dashboard";
import TranslationWindow from "@/pages/TranslationWindow";
import TranslationWindow2 from "@/pages/TranslationWindow2";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import DiagnosticsPage from "@/pages/DiagnosticsPage";
import PublicRoomPage from "@/pages/PublicRoomPage";
import { Toaster } from "@/components/ui/sonner";

// Protected route that requires authentication
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
}

// Protected route that redirects to home if no language selected
function RequireLanguage({ children }) {
  const { hasSelectedLanguage } = useLanguage();
  
  if (!hasSelectedLanguage) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Check if translation pair has been selected
function RequireTranslationPair({ children }) {
  const { hasSelectedLanguage } = useLanguage();
  const hasTranslationPair = localStorage.getItem("translation_target_lang");
  
  if (!hasSelectedLanguage) {
    return <Navigate to="/" replace />;
  }
  
  if (!hasTranslationPair) {
    return <Navigate to="/language-pair" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { hasSelectedLanguage } = useLanguage();
  const { isAuthenticated, loading } = useAuth();
  const hasTranslationPair = localStorage.getItem("translation_target_lang");
  const [forceShowContent, setForceShowContent] = useState(false);
  
  // Force show content after 3 seconds to prevent infinite loading on iOS
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn("[APP] Auth loading took too long, forcing content display");
        setForceShowContent(true);
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [loading]);
  
  // Log current state for debugging
  useEffect(() => {
    console.log("[APP] State - loading:", loading, "isAuthenticated:", isAuthenticated, "typeof isAuthenticated:", typeof isAuthenticated, "forceShowContent:", forceShowContent);
  }, [loading, isAuthenticated, forceShowContent]);
  
  // Show loading while checking auth (with timeout protection)
  if (loading && !forceShowContent) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        <p className="ml-3 text-white text-sm">Carregando...</p>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          hasSelectedLanguage 
            ? (hasTranslationPair ? <Navigate to="/dashboard" replace /> : <Navigate to="/language-pair" replace />) 
            : <Home />
        } 
      />
      <Route 
        path="/auth" 
        element={
          isAuthenticated 
            ? <Navigate to="/dashboard" replace /> 
            : <AuthPage />
        } 
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/diagnostics" element={<DiagnosticsPage />} />
      <Route 
        path="/account" 
        element={
          <RequireAuth>
            <AccountSettingsPage />
          </RequireAuth>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/language-pair" 
        element={
          <RequireAuth>
            <RequireLanguage>
              <LanguagePairSelection />
            </RequireLanguage>
          </RequireAuth>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <RequireAuth>
            <RequireTranslationPair>
              <Dashboard />
            </RequireTranslationPair>
          </RequireAuth>
        } 
      />
      <Route path="/translation" element={<TranslationWindow />} />
      <Route path="/translation2" element={<TranslationWindow2 />} />
      
      {/* Public room for visitors - no auth required */}
      <Route path="/sala/:roomId" element={<PublicRoomPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-zinc-950">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
